import { useState } from 'react';
import { Stack, Group, Switch } from '@mantine/core';
import Text from '@ui/components/Text/Text';
import Icon from '@ui/components/Icon/Icon';

import useToggle from '../../../../../hooks/useToggle';
import { trackProjectLinkShared } from '../../../../../services/tracker/trackers/projects';
import ShareLinkButton from '../../../../../components/ShareLinkButton/ShareLinkButton';
import Tooltip from '../../../../../components/UI/Tooltip/Tooltip';

import './ShareViewOnlyLink.scss';

export type Shareable = {
	shareLink: ( { withDownloads } : { withDownloads: boolean } ) => string
}

const linkContainerStyle = {
	height: 36,
	borderRadius: 4,
	border: '1px solid var(--mantine-color-gray-3)',
	padding: 6
}

const ShareViewOnlyLink = ( { shareable }: { shareable: Shareable } ) => {
	const [ allowsDownload, toggleAllowsDownload ] = useToggle( true );
	const [ showSuccessMessage, setShowSuccessMessage ] = useState( false );
	const shareLink = `${document.location.origin}/${shareable.shareLink( { withDownloads: allowsDownload } )}`;

	const copyShareLink = () => {
		navigator.clipboard.writeText( shareLink );
		setShowSuccessMessage( true );
		trackProjectLinkShared( { allowsDownload } );
		setTimeout( () => setShowSuccessMessage( false ), 2000 );
	};

	const tooltipData = (
		<p className="tooltip-text">Anyone who has this link can download<br />the most recent round of images</p>
	);

	return (
		<Stack className="ShareViewOnlyLink" p={16} spacing={16}>
			<Stack spacing={8}>
				<Text variant="body-bold">Permissions</Text>
				<Group position="apart">
					<Group spacing={6}>
						<Tooltip
							html={tooltipData}
							position="right"
						>
							<Icon
								icon="download-outlined"
								size={22}
								color="dark.3"
							/>
						</Tooltip>
						<Text>Allow downloads</Text>
					</Group>
					<Switch
						w={42}
						checked={allowsDownload}
						onChange={toggleAllowsDownload}
					/>
				</Group>
			</Stack>
			<Group px={6} py={4} spacing={4} bg="gray.0" style={linkContainerStyle} noWrap>
				<Text variant="footnote" color="dark.3" lineClamp={1}>{shareLink}</Text>
				<ShareLinkButton
					showSuccessMessage={showSuccessMessage}
					copyShareLink={copyShareLink}
				/>
			</Group>
			<Text variant="footnote" color="dark.3">
				View only links are public. Anyone with a view only link can <strong>access</strong> and{' '}
				<strong>view</strong> content <strong>anonymously</strong> -{' '}
				<strong>No account required.</strong>
			</Text>
		</Stack>
	);
};

export default ShareViewOnlyLink;
